/**
 * The ten-role ladder — PRODUCT taxonomy, not demo seed.
 *
 * Re-homed out of `src/demo/data.ts` by PX-W6.6 (TASK-188E6E40, spec §6.3,
 * invariant I-2): `RoleKey` is what `deriveRoleKey` maps backend signals onto,
 * what the session slice stores and what `canAccess` is keyed by, so product
 * furniture must not reach into the demo module for it.
 *
 * What stays HERE is the ladder itself — key, label, side. What stays in
 * `src/demo/data.ts` is the SEED BINDING of each role to a fictional person,
 * contact, client and project; `demo/data.ts` builds its `ROLES` from
 * {@link ROLE_DEFINITIONS} so the label and side can never drift between the
 * two, and re-exports `RoleKey`/`PrincipalSide` so the authored demo-content
 * modules need no edit.
 *
 * ⚠ Access POLICY does not live here (memory `role-access-is-the-product-claim`):
 * `src/access/roleAccess.ts` holds `ROLE_ACCESS` and `canAccess`, and stays the
 * single enforcement point for destinations.
 */

/**
 * Ten role keys. The August-2026 deck's eight became ten: `'client'` was split
 * into `'client-manager'` (the sponsor) and `'client-employee'` (a key user on
 * one project), and `'super-admin'` was added as the platform principal that
 * spans implementer firms.
 */
export type RoleKey =
  | 'project-manager'
  | 'consultant'
  | 'manager'
  | 'hr'
  | 'expert'
  | 'analyst'
  | 'admin'
  | 'super-admin'
  | 'client-manager'
  | 'client-employee'

/** Which population a principal belongs to — it decides how a persona resolves. */
export type PrincipalSide = 'firm' | 'client' | 'platform'

export interface RoleDefinition {
  key: RoleKey
  /**
   * A GENERIC, tenant-neutral English label — the last-resort fallback for a
   * caller with no i18n context (a log line, a test). Product furniture renders
   * {@link RoleDefinition.labelKey} through `t()` instead.
   *
   * ⚠ Never name a customer, a firm or a person here. Two of these labels used
   * to read `Client · Van Dijk`, carried over from the demo seed, and W6.6's
   * first cut rendered that fictional client's name into the live shell's
   * identity chip for every real client-side session (merged review, C1).
   * `src/access/fixtureNames.test.ts` now fails on any fixture name reaching a
   * product file.
   */
  label: string
  /** The `common` namespace key the UI renders — see {@link RoleDefinition.label}. */
  labelKey: string
  side: PrincipalSide
}

const definition = (key: RoleKey, label: string, side: PrincipalSide): RoleDefinition => ({
  key,
  label,
  labelKey: `roles.${key}`,
  side,
})

export const ROLE_DEFINITIONS: readonly RoleDefinition[] = [
  definition('project-manager', 'Project manager', 'firm'),
  definition('consultant', 'Consultant', 'firm'),
  definition('manager', 'Manager', 'firm'),
  definition('hr', 'HR', 'firm'),
  definition('expert', 'Expert', 'firm'),
  definition('analyst', 'Analyst', 'firm'),
  definition('admin', 'PX admin', 'firm'),
  definition('super-admin', 'Super admin', 'platform'),
  definition('client-manager', 'Client sponsor', 'client'),
  definition('client-employee', 'Client user', 'client'),
] as const

/** Every key in ladder order — the totality source for `Record<RoleKey, …>` audits. */
export const ROLE_KEYS: readonly RoleKey[] = ROLE_DEFINITIONS.map((role) => role.key)

/** Throws on an unknown key, matching the `personById` / `projectById` idiom. */
export const roleDefinitionFor = (key: RoleKey): RoleDefinition => {
  const definition = ROLE_DEFINITIONS.find((role) => role.key === key)
  if (!definition) throw new Error(`Unknown role: ${key}`)
  return definition
}

/** True for the two roles that sit on the client's side of the engagement. */
export const isClientSideRole = (key: RoleKey): boolean =>
  roleDefinitionFor(key).side === 'client'

/**
 * The lens a store with no derived session role looks through. It is a
 * RENDERING default only — never an access grant: `RoleGuarded` refuses on a
 * null `session.roleKey` before any surface reads this.
 */
export const DEFAULT_ROLE_KEY: RoleKey = 'project-manager'
